import React, { useEffect, useState } from 'react';
import { CalendarClock, Mail } from 'lucide-react';
import { saveScheduledReports } from '../utils/emailActivity';

const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

function normalizeReport(report) {
  const days = Array.isArray(report.days) && report.days.length
    ? report.days
    : report.id === 'weekly-digest'
      ? ['mon']
      : DAYS.map(day => day.key);
  return {
    time: report.time || (report.id === 'weekly-digest' ? '09:00' : '08:00'),
    ...report,
    days,
  };
}

function scheduleLabel(report) {
  const dayLabels = DAYS
    .filter(day => report.days?.includes(day.key))
    .map(day => day.label);
  const days = dayLabels.length === DAYS.length ? 'Every day' : dayLabels.join(', ') || 'No days';
  return `${days} · ${report.time || '08:00'} IST`;
}

function ScheduledReportsView({ reports = [], onReportsChange }) {
  const [localReports, setLocalReports] = useState(() => reports.map(normalizeReport));
  const [error, setError] = useState('');

  useEffect(() => {
    setLocalReports(reports.map(normalizeReport));
  }, [reports]);

  const persist = async (next) => {
    setLocalReports(next);
    setError('');
    try {
      const saved = await saveScheduledReports(next);
      const normalized = saved.map(normalizeReport);
      setLocalReports(normalized);
      onReportsChange?.(normalized);
    } catch (err) {
      setError(err.message || 'Failed to save scheduler');
    }
  };

  const handleToggle = (reportId) => {
    persist(localReports.map(r => (
      r.id === reportId ? { ...r, enabled: !r.enabled } : r
    )));
  };

  const updateTime = (reportId, time) => {
    persist(localReports.map(r => (
      r.id === reportId ? { ...r, time, schedule: scheduleLabel({ ...r, time }) } : r
    )));
  };

  const toggleDay = (reportId, dayKey) => {
    persist(localReports.map(r => {
      if (r.id !== reportId) return r;
      const days = r.days?.includes(dayKey)
        ? r.days.filter(day => day !== dayKey)
        : [...(r.days || []), dayKey];
      const updated = { ...r, days };
      return { ...updated, schedule: scheduleLabel(updated) };
    }));
  };

  return (
    <div className="space-y-3">
      {error && <p className="text-xs text-[#bd2f2b]">{error}</p>}
      {localReports.map(report => (
        <div
          key={report.id}
          className={`bg-white rounded-lg border border-[#e1e5ec] p-[18px] transition ${report.enabled ? '' : 'opacity-75'}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-[15px] font-semibold text-[#0d1524]">{report.name}</h3>
              <p className="text-[13px] text-[#45516a] mt-1 flex items-center gap-1.5">
                <CalendarClock size={14} className="text-[#76829a]" />
                {scheduleLabel(report)}
              </p>
              <p className="text-[13px] text-[#45516a] mt-1.5 flex items-center gap-1.5">
                <Mail size={14} className="text-[#76829a] flex-shrink-0" />
                <span className="truncate">{(report.recipients || []).join(', ') || 'No recipients configured'}</span>
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={Boolean(report.enabled)}
              aria-label={`${report.name} ${report.enabled ? 'active' : 'paused'}`}
              onClick={() => handleToggle(report.id)}
              className={`relative flex-none w-9 h-5 rounded-full transition ${report.enabled ? 'bg-[#1c4ed8]' : 'bg-[#cbd3df]'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${report.enabled ? 'translate-x-4' : ''}`} />
            </button>
          </div>
          <div className="mt-4 pt-4 border-t border-[#e1e5ec] grid grid-cols-1 md:grid-cols-[160px_minmax(0,1fr)] gap-4">
            <label className="block">
              <span className="text-xs font-medium text-[#5b6780] block mb-1.5">Delivery time</span>
              <input
                type="time"
                value={report.time || '08:00'}
                onChange={e => updateTime(report.id, e.target.value)}
                className="w-full h-8 text-[13px] border border-[#cbd3df] rounded-md px-2.5 focus:outline-none focus:border-[#1c4ed8]"
              />
            </label>
            <div>
              <span className="text-xs font-medium text-[#5b6780] block mb-1.5">Delivery days</span>
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map(day => {
                  const active = report.days?.includes(day.key);
                  return (
                    <button
                      key={day.key}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleDay(report.id, day.key)}
                      className={`h-8 px-3 rounded-md border text-[13px] font-medium transition ${
                        active ? 'bg-[#1c4ed8] border-[#1c4ed8] text-white' : 'bg-white border-[#cbd3df] text-[#45516a] hover:bg-[#f6f7fa]'
                      }`}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default ScheduledReportsView;
